import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import i18n from "../i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed

  useEffect(() => {
    const token = localStorage.getItem("lp_token");
    if (!token) {
      setUser(false);
      return;
    }
    api.get("/auth/me")
      .then(({ data }) => {
        if (data.settings?.language && data.settings.language !== i18n.language) {
          i18n.changeLanguage(data.settings.language);
        }
        setUser(data);
      })
      .catch(() => {
        localStorage.removeItem("lp_token");
        setUser(false);
      });
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("lp_token", data.token);
    // restore the user's saved language preference from the database
    const me = await api.get("/auth/me");
    if (me.data.settings?.language && me.data.settings.language !== i18n.language) {
      i18n.changeLanguage(me.data.settings.language);
    }
    setUser(me.data);
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem("lp_token", data.token);
    // persist the newly-detected UI language as the new user's preference
    const detected = ["en", "it", "fr", "es"].includes(i18n.language) ? i18n.language : "en";
    try { await api.patch("/auth/settings", { language: detected }); } catch (e) { /* ignore */ }
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch (e) {
      setUser(data.user);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    localStorage.removeItem("lp_token");
    setUser(false);
  };

  const refreshUser = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) { return null; }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
